/** Number, date and placeholder-art helpers shared by every screen. */

import type { Lang } from './i18n';

const FA_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];

export function toFaDigits(s: string | number): string {
  return String(s).replace(/[0-9]/g, (d) => FA_DIGITS[Number(d)]!);
}

export function localizeDigits(s: string | number, lang: Lang): string {
  return lang === 'fa' ? toFaDigits(s) : String(s);
}

/** 1200 -> "1.2k". Keeps stat rows to a fixed width. */
/**
 * A count, or nothing at all.
 *
 * `compactNumber` turns anything non-finite into the string "0", which is the right answer for a
 * download count and the wrong one for a chip. A category chip reading "Portraits 0" does not say
 * "we could not load the number", it says "this category is empty" — it actively talks the user
 * out of tapping, and it is what every chip said whenever the taxonomy response omitted `count`.
 *
 * Absent and zero are the same thing here on purpose: a category with nothing in it should not be
 * advertising the fact, and V8's rule was that dead categories do not appear at all.
 */
export function optionalCount(n: number | undefined | null, lang: Lang): string | undefined {
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? compactNumber(n, lang) : undefined;
}

/** `"Video · ۱۲۸"`, or just `"Video"` when there is no number worth printing. */
export function withCount(label: string, n: number | undefined | null, lang: Lang): string {
  const c = optionalCount(n, lang);
  return c ? `${label} · ${c}` : label;
}

export function compactNumber(n: number, lang: Lang): string {
  const v = Number.isFinite(n) ? n : 0;
  let s: string;
  if (v >= 1_000_000) s = `${(v / 1_000_000).toFixed(1).replace('.0', '')}M`;
  else if (v >= 1000) s = `${(v / 1000).toFixed(1).replace('.0', '')}k`;
  else s = String(Math.round(v));
  return localizeDigits(s, lang);
}

/** One decimal, for star averages. */
export function decimal1(n: number, lang: Lang): string {
  return localizeDigits((Number.isFinite(n) ? n : 0).toFixed(1), lang);
}

export function timeAgo(ts: number, lang: Lang): string {
  const diff = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  const fmt = (v: number, en: string, fa: string) =>
    lang === 'fa' ? `${toFaDigits(v)} ${fa}` : `${v}${en}`;
  if (diff < 60) return lang === 'fa' ? 'همین حالا' : 'now';
  if (diff < 3600) return fmt(Math.floor(diff / 60), 'm', 'دقیقه');
  if (diff < 86400) return fmt(Math.floor(diff / 3600), 'h', 'ساعت');
  if (diff < 2_592_000) return fmt(Math.floor(diff / 86400), 'd', 'روز');
  return fmt(Math.floor(diff / 2_592_000), 'mo', 'ماه');
}

export function clockTime(ts: number, lang: Lang): string {
  const d = new Date(ts);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return localizeDigits(`${hh}:${mm}`, lang);
}

export function isSameDay(a: number, b: number): boolean {
  const x = new Date(a);
  const y = new Date(b);
  return (
    x.getFullYear() === y.getFullYear() &&
    x.getMonth() === y.getMonth() &&
    x.getDate() === y.getDate()
  );
}

/** Stable 32-bit hash — same prompt id always yields the same placeholder art. */
export function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function hueFor(seed: string): number {
  return hashString(seed) % 360;
}

/**
 * Deterministic gradient stand-in for a missing example image, ported from the design's
 * three-layer oklch build. Doubles as the blur-up backdrop behind real images, so a slow
 * photo fades in over colour rather than over a grey hole.
 */
export function artBackground(seed: string, variant = 0): string {
  const hue = hueFor(seed);
  const h2 = (hue + 34) % 360;
  const h3 = (hue + 296) % 360;
  const sets = [
    `radial-gradient(105% 85% at 24% 14%, oklch(0.9 0.1 ${h2}) 0%, transparent 58%), radial-gradient(85% 78% at 82% 32%, oklch(0.63 0.16 ${hue}) 0%, transparent 62%), radial-gradient(150% 125% at 48% 112%, oklch(0.31 0.1 ${h3}) 0%, oklch(0.16 0.045 ${h3}) 74%)`,
    `radial-gradient(80% 70% at 74% 18%, oklch(0.87 0.12 ${hue}) 0%, transparent 55%), radial-gradient(120% 90% at 12% 62%, oklch(0.55 0.15 ${h3}) 0%, transparent 60%), radial-gradient(140% 140% at 60% 104%, oklch(0.26 0.09 ${h2}) 0%, oklch(0.14 0.04 ${hue}) 78%)`,
    `radial-gradient(95% 95% at 50% 8%, oklch(0.84 0.13 ${h3}) 0%, transparent 52%), radial-gradient(90% 80% at 20% 40%, oklch(0.6 0.17 ${h2}) 0%, transparent 58%), radial-gradient(160% 130% at 78% 108%, oklch(0.29 0.1 ${hue}) 0%, oklch(0.15 0.04 ${h3}) 76%)`,
  ];
  return sets[Math.abs(variant) % 3]!;
}

/** Avatar tint for a name with no photo — same hue family as the card art. */
export function avatarBackground(seed: string): string {
  return `oklch(0.72 0.13 ${hueFor(seed)})`;
}

export function initialOf(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  // Take the first grapheme so an emoji or a Farsi letter doesn't get sliced apart.
  return [...trimmed][0]!.toUpperCase();
}

export function formatDuration(seconds: number | null, lang: Lang): string | null {
  if (seconds === null || !Number.isFinite(seconds) || seconds <= 0) return null;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return localizeDigits(`${m}:${String(s).padStart(2, '0')}`, lang);
}
